import { inject, Injectable, DOCUMENT } from '@angular/core';
import { LocalStorageService } from '../storage/local-storage.service';


@Injectable({ providedIn: 'root' })
export class AuthCryptoService {
  private readonly document = inject(DOCUMENT);
  private readonly window = this.document?.defaultView;

  public constructor(private _localStore: LocalStorageService) {}

  public async generateCodeVerifier(): Promise<string> {
    // the psfy idp config says one UUID (32 chars without the hyphens) isn't long enough, so double down.
    const uuid = this.window?.crypto.randomUUID() + this.window?.crypto.randomUUID();
    const key = uuid.replace(/-/g, '');

    // store the code verifier BEFORE we hash & URL encode it
    let expirationDate = new Date();
    expirationDate.setMinutes(expirationDate.getMinutes() + 5);
    this._localStore.setPerishableObject<string>('psfy-idp-code-verifier', key, expirationDate);

    let hashBuffer = await this.encodeSha256(key);
    let codeVerifier = this.base64UrlEncode(hashBuffer);

    return codeVerifier;
  }

  public retrieveCodeVerifier(): string {
    const codeVerifier = this._localStore.getPerishableObject<string>('psfy-idp-code-verifier');
    this._localStore.removeItem('psfy-idp-code-verifier');

    return codeVerifier;
  }

  public generateState(): string {
    let buffer = new Uint8Array(8);
    buffer = window?.crypto.getRandomValues(buffer);

    const first = buffer.slice(0, 3).join('');
    const last = buffer.slice(4, 7).join('');
    const state = first + '.' + last;

    let expirationDate = new Date();
    expirationDate.setMinutes(expirationDate.getMinutes() + 5);
    this._localStore.setPerishableObject<string>('psfy-idp-state', state, expirationDate);

    return state;
  }

  public retreiveState(): string {
    const state = this._localStore.getPerishableObject<string>('psfy-idp-state');
    this._localStore.removeItem('psfy-idp-state');

    return state;
  }

  public generateNonce(): string {
    let buffer = new Uint8Array(8);
    buffer = window?.crypto.getRandomValues(buffer);

    const first = buffer.slice(0, 3).join('');
    const last = buffer.slice(4, 7).join('');
    const nonce = first + '.' + last;
    let expirationDate = new Date();
    expirationDate.setMinutes(expirationDate.getMinutes() + 5);
    this._localStore.setPerishableObject<string>('psfy-idp-nonce', nonce, expirationDate);

    return nonce;
  }

  public retreiveNonce(): string {
    const state = this._localStore.getPerishableObject<string>('psfy-idp-nonce');
    this._localStore.removeItem('psfy-idp-nonce');

    return state;
  }

  private encodeSha256(plaintext: string) {
    const encoder = new TextEncoder();
    const data = encoder.encode(plaintext);
    return window?.crypto.subtle.digest('SHA-256', data);
  }

  private base64UrlEncode(buffer: ArrayBuffer) {
    return btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }
}
